import { type ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { TestPhaseBanner } from '@/components/test-phase-banner';
import { WalletWarningBanner } from '@/components/wallet-warning-banner';
import { WeeklyRiskBanner } from '@/components/weekly-risk-banner';

const BANNER_BUFFER_PX = 24;

function useFloatingHeight(): {
  ref: React.RefObject<HTMLDivElement | null>;
  height: number;
} {
  const ref = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = (): void => setHeight(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return { ref, height };
}

function BannerStack({
  stackRef,
}: {
  stackRef: React.RefObject<HTMLDivElement | null>;
}): ReactNode {
  return (
    <div
      ref={stackRef}
      className="pointer-events-none absolute inset-x-0 top-0 z-20 space-y-sm px-lg pt-lg empty:hidden md:px-xl md:pt-xl [&>*]:pointer-events-auto"
    >
      <TestPhaseBanner />
      <WeeklyRiskBanner />
      <WalletWarningBanner />
    </div>
  );
}

export function FloatingBanners({
  children,
}: {
  children: ReactNode;
}): ReactNode {
  const { ref, height } = useFloatingHeight();
  const pad = height > 0 ? height + BANNER_BUFFER_PX : undefined;
  return (
    <>
      <BannerStack stackRef={ref} />
      <main
        className="flex-1 overflow-y-auto p-lg md:p-xl"
        style={pad ? { paddingTop: pad } : undefined}
      >
        {children}
      </main>
    </>
  );
}
